// Intent / signature page layout: customer sample toggle + configurable
// fill rows (signature / initials / date / freeform text) with optional
// variable keys for autofill (e.g. revision_number).

import type { Opportunity, OpportunityPricingSnapshot } from "../../types/opportunity.js";
import { formatOpportunityCurrency } from "./formatCurrency.js";

export const INTENT_FILL_KINDS = ["signature", "initials", "date", "text"] as const;
export type IntentFillKind = (typeof INTENT_FILL_KINDS)[number];

export type IntentFillField = {
  id: string;
  kind: IntentFillKind;
  label: string;
  /** Optional linked value (revision_number, total_sale_price, …). */
  variable_key?: string | null;
};

export type IntentSignatureParty = {
  id: string;
  title: string;
  fields: IntentFillField[];
};

export type IntentPageLayout = {
  show_customer_sample: boolean;
  signature_parties: IntentSignatureParty[];
};

export const INTENT_AUTOFILL_VARIABLE_KEYS = [
  "revision_number",
  "total_sale_price",
  "deposit_amount",
  "customer_name",
  "customer_company",
  "project_name",
] as const;

export type IntentAutofillVariableKey = (typeof INTENT_AUTOFILL_VARIABLE_KEYS)[number];

export const INTENT_AUTOFILL_VARIABLE_LABELS: Record<IntentAutofillVariableKey, string> = {
  revision_number: "Revision number",
  total_sale_price: "Total sale price",
  deposit_amount: "Deposit at signing",
  customer_name: "Customer name",
  customer_company: "Customer company",
  project_name: "Project name",
};

function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function defaultIntentSignatureParties(): IntentSignatureParty[] {
  return [
    {
      id: "customer",
      title: "Customer signature",
      fields: [
        { id: "cust_sig", kind: "signature", label: "Signature" },
        { id: "cust_name", kind: "text", label: "Printed name" },
        { id: "cust_date", kind: "date", label: "Date" },
      ],
    },
    {
      id: "company",
      title: "Company representative",
      fields: [
        { id: "co_sig", kind: "signature", label: "Signature" },
        { id: "co_name", kind: "text", label: "Printed name" },
        { id: "co_date", kind: "date", label: "Date" },
      ],
    },
  ];
}

export function defaultIntentPageLayout(): IntentPageLayout {
  return {
    show_customer_sample: true,
    signature_parties: defaultIntentSignatureParties(),
  };
}

function isFillKind(v: unknown): v is IntentFillKind {
  return typeof v === "string" && (INTENT_FILL_KINDS as readonly string[]).includes(v);
}

function normalizeField(raw: unknown, index: number): IntentFillField | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (!isFillKind(o.kind)) return null;
  const label = typeof o.label === "string" && o.label.trim() ? o.label.trim() : o.kind;
  const id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : `field_${index}`;
  const variable_key =
    typeof o.variable_key === "string" && o.variable_key.trim()
      ? o.variable_key.trim()
      : null;
  return { id, kind: o.kind, label, variable_key };
}

function normalizeParty(raw: unknown, index: number): IntentSignatureParty | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const title =
    typeof o.title === "string" && o.title.trim()
      ? o.title.trim()
      : `Party ${index + 1}`;
  const id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : `party_${index}`;
  const fieldsRaw = Array.isArray(o.fields) ? o.fields : [];
  const fields = fieldsRaw
    .map((f, i) => normalizeField(f, i))
    .filter((f): f is IntentFillField => f != null);
  if (fields.length === 0) {
    fields.push(
      { id: newId("sig"), kind: "signature", label: "Signature" },
      { id: newId("date"), kind: "date", label: "Date" },
    );
  }
  return { id, title, fields };
}

/** Normalize layout fields from jsonb (missing → defaults matching legacy PDF). */
export function normalizeIntentPageLayout(raw: unknown): IntentPageLayout {
  const fallback = defaultIntentPageLayout();
  if (!raw || typeof raw !== "object") return fallback;
  const o = raw as Record<string, unknown>;
  const show_customer_sample = o.show_customer_sample !== false;
  const partiesRaw = Array.isArray(o.signature_parties) ? o.signature_parties : null;
  if (!partiesRaw || partiesRaw.length === 0) {
    return { show_customer_sample, signature_parties: fallback.signature_parties };
  }
  const signature_parties = partiesRaw
    .map((p, i) => normalizeParty(p, i))
    .filter((p): p is IntentSignatureParty => p != null);
  return {
    show_customer_sample,
    signature_parties:
      signature_parties.length > 0 ? signature_parties : fallback.signature_parties,
  };
}

export function resolveIntentAutofillValue(args: {
  variableKey: string | null | undefined;
  opportunity: Opportunity;
  snapshot: OpportunityPricingSnapshot;
  revisionNumber: number | null;
}): string | null {
  const key = args.variableKey?.trim();
  if (!key) return null;
  switch (key) {
    case "revision_number":
      return args.revisionNumber != null ? String(args.revisionNumber) : null;
    case "total_sale_price":
      return formatOpportunityCurrency(args.snapshot.total_sale_price);
    case "deposit_amount":
      return formatOpportunityCurrency(args.snapshot.deposit_amount);
    case "customer_name":
      return args.opportunity.customer_name?.trim() || null;
    case "customer_company":
      return args.opportunity.customer_company?.trim() || null;
    case "project_name":
      return args.opportunity.project_name?.trim() || null;
    default:
      return null;
  }
}

export function createEmptyIntentFillField(kind: IntentFillKind = "text"): IntentFillField {
  const labels: Record<IntentFillKind, string> = {
    signature: "Signature",
    initials: "Initials",
    date: "Date",
    text: "Printed name",
  };
  return { id: newId(kind), kind, label: labels[kind], variable_key: null };
}

export function createEmptyIntentSignatureParty(): IntentSignatureParty {
  return {
    id: newId("party"),
    title: "Signature",
    fields: [
      createEmptyIntentFillField("signature"),
      createEmptyIntentFillField("text"),
      createEmptyIntentFillField("date"),
    ],
  };
}
